/**
 * Created by Ning Jiang on 2021-05-06.
 */

import {gsap} from "gsap";
import {IReelStateEventData, ReelState} from "../../core/reel/ReelState";
import {DeadwoodRipSymbol} from "../symbol/DeadwoodRipSymbol";
import {DeadwoodRipReelView} from "./DeadwoodRipReelView";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {MathHelper} from "../../core/utils/MathHelper";
import {IParsedStackedSymbolName, StackedSymbol} from "../../core/reel/stackedsymbol/StackedSymbol";
import {DeadwoodRip} from "../DeadwoodRip";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {DeadwoodRipGameConfig} from "../DeadwoodRipGameConfig";
import {DeadwoodRipSymbolConfig, DeadwoodRipSymbolState} from "../symbol/DeadwoodRipSymbolConfig";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {DeadwoodRipStackedSymbol} from "../symbol/DeadwoodRipStackedSymbol";
import {DeadwoodRipStackedWild} from "../symbol/DeadwoodRipStackedWild";
import {DeadwoodRipS1Symbol} from "../symbol/DeadwoodRipS1Symbol";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {RetriggerS1Symbol} from "../symbol/RetriggerS1Symbol";
import {CylinderSymbol} from "../symbol/CylinderSymbol";
import {DeadwoodRipCharacterSymbol} from "../symbol/DeadwoodRipCharacterSymbol";
import {DeadwoodRipLSymbol} from "../symbol/DeadwoodRipLSymbol";
import {DeadwoodRipS3Symbol} from "../symbol/DeadwoodRipS3Symbol";

export class DeadwoodRipReelController {
    public static SYMBOLS_NUM_IN_DATA:number[][] = [
        [0, 1, 3, 1, 0],
        [0, 1, 4, 1, 0],
        [0, 1, 4, 1, 0],
        [0, 1, 4, 1, 0],
        [0, 1, 2, 1, 0]
    ];

    public readonly events:APIEventSystem;

    public reelId:number;
    public reelView:DeadwoodRipReelView;
    public isQuickStop:boolean;

    private readonly _symbolsNumInData:number[]; // see legacy doc wiki about reel config.
    private _symsNumTotal:number;
    private _currentReelSet:string[]; //The reelSet currently in use, it might be different than the actually reelSet because its made of many different parts.
    private _currentFullReelSet:string[]; // The full actually reelSet that the most part of the _currentReelSet is make of.
    private _stopReelSetFilledBottomNum:number = 0; // Use to calculate added symbols under the server reel symbols when stopping.
    private _extraStopDelay:number;
    private _reelStopDelayTimer:TweenLite | null; //Delay for reel stop
    private _stopCounter:number = 0;

    private _normalStartStepDuration:number = 0.3;
    private _fastStartStepDuration:number = 0.2;
    private _startEase:string = `back.in(4)`;
    private _defaultSpinStepDuration:number = 0.033;
    private _normalStopStepDuration:number = 0.5;
    private _fastStopStepDuration:number = 0.4;
    private _normalStopEase:string = `elastic.out(1.5, 0.95)`;
    private _fastStopEase:string = `elastic.out(1.2, 0.75)`;

    private _abortNudge:boolean;
    private _nudgeTimeline:TimelineLite;
    private _removedSymbolPool:DeadwoodRipSymbol[] = [];
    private _maxRemovedSymbolPoolLength:number = 4;

    private _reelState:ReelState;
    get reelState():ReelState {
        return this._reelState;
    }

    set reelState(value:ReelState) {
        if(value === this._reelState) {
            return;
        }

        const oldState = this._reelState;
        this._reelState = value;
        this.events.trigger("ReelStateChange", <IReelStateEventData>{
            reelId : this.reelId,
            newState : this._reelState,
            oldState : oldState
        });
    }

    constructor(reelId:number) {
        this.events = SlotGame.apiPlugin.eventSystemFactory.create();
        this.events.on("ReelStateChange", (data:IReelStateEventData) => this.events.trigger(data.newState, data)); //To prevent event system from saving once callbacks if no on callbacks is set.

        this.reelId = reelId;
        this.reelState = ReelState.IDLE;
        this._symbolsNumInData = DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA[reelId];
        this._symsNumTotal = ArrayHelper.arraySum(this._symbolsNumInData);
        this.reelView = new DeadwoodRipReelView(reelId);
        this.initSymbols();
    }

    private initSymbols():void {
        this._currentReelSet = this.makeUpStopReelSet(true);
        if(this._stopReelSetFilledBottomNum < this._symbolsNumInData[4]) {
            this._currentReelSet = this._currentReelSet.concat(this.makeUpStopReelSetRandomFillUp(this._symbolsNumInData[4] - this._stopReelSetFilledBottomNum));
        }

        for(let i:number = 0; i < this._symsNumTotal; i++) {
            this.addNextNewSymbol(DeadwoodRipSymbolState.NORMAL);
        }
        this.reelView.initSymbolsPosition(this._symbolsNumInData[0] + this._symbolsNumInData[1]);
    }

    private getReelSet(name:string):string[] {
        if(DeadwoodRipGameConfig.REEL_SETS[name]) {
            const reelSet:string[] = DeadwoodRipGameConfig.REEL_SETS[name][this.reelId];
            if(reelSet.length < 5) {
                Logger.logDev(`ReelController.getReelSet() name = ${name}, reelId = ${this.reelId}, the reelSet is too short that might break ReelController.randomizeReelSetPosition().`)
            }
            return reelSet;
        }

        debugger;
        throw new Error(`ReelController.getReelSet(): ReelSet ${name} is missing from GameConfig!`);
    }

    // only for spin here
    private makeUpStopReelSet(init:boolean):string[] {
        const stopReelSet = DeadwoodRip.model.reels[this.reelId];
        const topFillUp:string[] = this.makeUpStackedSymbolTop(stopReelSet[0]);
        const bottomFillUp:string[] = this.makeUpStackedSymbolBottom(stopReelSet[stopReelSet.length - 1], init);
        this._currentFullReelSet = this.getReelSet(DeadwoodRipGameConfig.getReelSetName(DeadwoodRip.model.data.nextMode));
        const randomPosition:number = this.randomizeReelSetPosition(this._currentFullReelSet);
        return this._currentFullReelSet.slice(0, randomPosition + 1).concat(topFillUp).concat(stopReelSet).concat(bottomFillUp);
    }

    // Get a position where the symbol is not part of a stacked symbol unless it's the bottom of the stacked symbol.
    private randomizeReelSetPosition(reelSet:string[]):number {
        let position:number = MathHelper.randomNumberInRange(4, reelSet.length - 1); // Start on 10 to make sure there is enough number included.

        const parsedStackedName:IParsedStackedSymbolName = StackedSymbol.parseStackedSymbolName(reelSet[position], this.reelId);
        if(parsedStackedName.isStacked) {
            position += parsedStackedName.totalNum - 1 - parsedStackedName.index;
        }

        return position;
    }

    private makeUpStackedSymbolTop(targetSymName:string):string[] {
        let result:string[] = [];
        const stackedName:IParsedStackedSymbolName = StackedSymbol.parseStackedSymbolName(targetSymName, this.reelId);
        if(stackedName.isStacked && stackedName.index > 0) {
            for(let i:number = 0; i < stackedName.index; i++) {
                result.push(`${stackedName.symName}*${i}`);
            }
        }
        return result;
    }

    private makeUpStackedSymbolBottom(aboveSymName:string, init:boolean):string[] {
        let result:string[] = [];
        const stackedName:IParsedStackedSymbolName = StackedSymbol.parseStackedSymbolName(aboveSymName, this.reelId);
        if(stackedName.isStacked && stackedName.index < (stackedName.totalNum - 1)) {
            let stackedNum:number = init ? Math.min(stackedName.totalNum, stackedName.index + this._symbolsNumInData[4] + 1) : stackedName.totalNum;
            for(let i:number = stackedName.index + 1; i < stackedNum; i++) {
                result.push(`${stackedName.symName}*${i}`);
            }
        }
        this._stopReelSetFilledBottomNum = result.length;
        return result;
    }

    protected makeUpStopReelSetRandomFillUp(length:number):string[] {
        return ArrayHelper.initArrayWithValues(length, index => this.getRandomSymbolNameFromReelSet(this._currentFullReelSet));
    }

    private getRandomSymbolNameFromReelSet(reelSet:string[]):string {
        let symbolName:string;
        do {
            symbolName = ArrayHelper.getRandomValue<string>(reelSet);
        } while(symbolName.includes("W") || DeadwoodRipSymbolConfig.SYMBOLS[symbolName].isSpecial);

        return symbolName;
    }

    private addNextNewSymbol(state:DeadwoodRipSymbolState):DeadwoodRipSymbol {
        if(this._currentReelSet.length == 0) {
            if(this._currentFullReelSet == null) {
                debugger;
                throw new Error("ReelController.createNextSymbol():This is a bug that Ning is trying to reproduce!");
            }
            this._currentReelSet = this._currentFullReelSet.concat();
        }
        const symName:string = this._currentReelSet.pop()!;
        if(symName == null || symName.length === 0) {
            debugger;
            throw new Error(`ReelController.addNextNewSymbol(): symbol name cannot be empty!`);
        }
        let symbol:DeadwoodRipSymbol = this.createSymbol(symName, this.reelId, state);
        this.reelView.addSymbol(symbol);
        this.reelView.allSymbols.unshift(symbol);

        const previousSymbol = this.reelView.allSymbols[1];
        StackedSymbol.updateStackedSymbols(symbol, previousSymbol, -1, true);
        if(previousSymbol) {
            symbol.y = previousSymbol.y - previousSymbol.symbolHeight * 0.5 - symbol.bottomPadding - symbol.symbolHeight * 0.5;
        }

        return symbol;
    }

    public createSymbol(symName:string, reelId:number, state:DeadwoodRipSymbolState):DeadwoodRipSymbol {
        if(symName.includes("WXN")) {
            return new DeadwoodRipStackedWild(symName, this.reelId, state);
        }
        if(symName === "S1") {
            return DeadwoodRip.model.data.mode === GameMode.NORMAL ? new DeadwoodRipS1Symbol(this.reelId, state) : new RetriggerS1Symbol(this.reelId, state);
        }

        if(symName === "S3") {
            return new DeadwoodRipS3Symbol(this.reelId, state);
        }

        if(symName === "C") {
            return new CylinderSymbol(reelId, state);
        }

        if(symName.includes("M")) {
            return new DeadwoodRipCharacterSymbol(symName, this.reelId, state);
        }

        if(symName.includes("L")) {
            return new DeadwoodRipLSymbol(symName, this.reelId, state);
        }

        return new DeadwoodRipSymbol(symName, this.reelId, state);
    }

    private removeBottomSymbol():void {
        const removeSymbol = this.reelView.allSymbols.pop()!;
        StackedSymbol.updateStackedSymbols(ArrayHelper.fetchLastValue(this.reelView.allSymbols), null, 1, false);
        this.reelView.removeSymbol(removeSymbol);
        this._removedSymbolPool.unshift(removeSymbol);
        if(this._removedSymbolPool.length > this._maxRemovedSymbolPoolLength) {
            this._removedSymbolPool.pop()!.onRemove();
        }
    }

    private setNextState(reelState:ReelState, duration?:number, ease?:string):void {
        this.reelView.nextSpinStepConfig = {
            reelState : reelState,
            duration : duration,
            ease : ease
        }
    }

    public startSpin(active:boolean, delay:number):Promise<any> {
        this._extraStopDelay = 0;
        this._stopCounter = 0;
        this.isQuickStop = false;
        this.reelView.wantBounce = false;

        if(!active) {
            this.reelState = ReelState.INACTIVE;
            return Promise.resolve();
        } else {
            this.setNextState(
                ReelState.START,
                SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) ? this._fastStartStepDuration : this._normalStartStepDuration,
                this._startEase
            );
            const tl = gsap.timeline({
                onComplete : () => {
                    if(active) {
                        this.setNextState(ReelState.SPIN, this._defaultSpinStepDuration, 'none');
                        this.spinStep();
                    }
                }
            });
            tl.add(this.reelView.step(this.addNextNewSymbol(DeadwoodRipSymbolState.SPIN), () => this.removeBottomSymbol()), delay);
            return new Promise<any>(res => {
                this.events.once(ReelState.SPIN, async (data:IReelStateEventData) => {
                    res(this);
                });
            });
        }
    }

    public spinStep():TimelineLite {
        const tl = gsap.timeline({
            onComplete : () => {
                if(this.reelState === ReelState.SPIN) {
                    this.spinStep();
                    return;
                }
                if(this.reelState === ReelState.STOPPING) {
                    if(this._stopCounter > 1) {
                        this._stopCounter--;
                        this.spinStep();
                        return;
                    }

                    this._stopCounter = 0;
                    this.stopStep();
                }
            }
        });
        tl.add(this.reelView.step(this.addNextNewSymbol(DeadwoodRipSymbolState.SPIN), () => this.removeBottomSymbol()));
        return tl;
    }

    public stopStep():TimelineLite {
        this.setNextState(
            ReelState.STOPPING,
            (SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) || this.isQuickStop) ? this._fastStopStepDuration : this._normalStopStepDuration,
            (SlotGame.apiPlugin.settings.get(APISetting.FAST_SPIN) || this.isQuickStop) ? this._fastStopEase : this._normalStopEase
        );
        const tl = gsap.timeline({
            onStart : () => {this.reelView.wantBounce = true;},
            onComplete : () => {this.reelState = ReelState.STOPPED;}
        });
        tl.add(this.reelView.step(this.addNextNewSymbol(DeadwoodRipSymbolState.NORMAL), () => this.removeBottomSymbol()));
        this.reelView.allSymbols.forEach((symbol) => {
            tl.add(() => {
                if(!symbol.transform) {
                    return;
                }
                symbol.getStateAnimation(DeadwoodRipSymbolState.NORMAL,
                    {duration : this.reelView.currentStepDuration, alpha : 1},
                    {duration : this.reelView.currentStepDuration, alpha : 0})
            }, 0);
        });
        return tl;
    }

    // The stop offset is for features on the reel to prolong the stop. like if the server gives a half stacked symbol in the bottom, so we need to add more symbols to fill it up.
    public getSpinStopOffset():number {
        if(this.reelState !== ReelState.SPIN) {
            return 0;
        }

        // check if there is unfinished stacked wilds in the bottom of the stop reel set.
        // When the offset is more than 0, we have to make sure to compensate it from the stop reel set.
        let spinStopOffset:number = 0;
        let bottomSymbol:string = ArrayHelper.fetchLastValue(DeadwoodRip.model.reels[this.reelId]);
        let parsedStackedName:IParsedStackedSymbolName = StackedSymbol.parseStackedSymbolName(bottomSymbol, this.reelId);
        if(parsedStackedName.isStacked && parsedStackedName.index < (parsedStackedName.totalNum - 1)) {
            spinStopOffset = parsedStackedName.totalNum - 1 - parsedStackedName.index;
        }

        return Math.max(0, spinStopOffset - this._symbolsNumInData[4]);
    }

    public hasNearWin():boolean {
        return DeadwoodRip.model.parsedGameData.nearWinBigWinReels[this.reelId] || DeadwoodRip.model.parsedGameData.nearWinReels[this.reelId];
    }

    public addStopDelay(value:number):void {
        this._extraStopDelay += value;
    }

    public addStopDelayIfNotEnough(value:number):void {
        // Logger.logDev(`reel ${this.reelId} go added delay ${value}, totalDelayBefore ${this._extraStopDelay}`);
        this._extraStopDelay = Math.max(value, this._extraStopDelay);
        // Logger.logDev(`totalDelayAfter ${this._extraStopDelay}`);
    }

    // This is just estimated time
    public getDefaultStoppingTime():number {
        return (this._symsNumTotal - 1) * this._defaultSpinStepDuration + this._normalStopStepDuration;
    }

    public tryStop(spinStopOffset:number, delay:number, quickStop:boolean, stopStartCallBack?:() => void):void {
        // Logger.logDev(`reel ${this.reelId} try to stop in delay ${delay}`);
        this.isQuickStop = quickStop;

        if(this.reelState === ReelState.INACTIVE) {
            // Logger.logDev(`reel ${this.reelId} is inactive, stopped.`);
            if(stopStartCallBack) {
                stopStartCallBack();
            }
            this.reelState = ReelState.STOPPED;
            return;
        }

        if(this.reelState === ReelState.STOPPING) {
            // Logger.logDev(`reel ${this.reelId} is already stopping.`);
            return;
        }

        if(this._reelStopDelayTimer && this._reelStopDelayTimer.isActive()) {
            this._reelStopDelayTimer.pause();
            this._reelStopDelayTimer.kill();
        }

        if(this.isQuickStop) {
            this._extraStopDelay = 0;
        }

        const totalDelay:number = delay + this._extraStopDelay;
        this._extraStopDelay = 0;
        // Logger.logDev(`reel ${this.reelId} try to stop in extra delay ${this._extraStopDelay}, total delay ${totalDelay}`);
        if(totalDelay > 0) {
            this._reelStopDelayTimer = gsap.to(this, totalDelay, {onComplete : () => this.tryStop(spinStopOffset, 0, quickStop, stopStartCallBack)});
            return;
        }

        // start stop
        if(stopStartCallBack) {
            stopStartCallBack();
        }
        this.getReadyToStop(spinStopOffset);
    }

    private getReadyToStop(spinStopOffset:number):void {
        // Logger.logDev(`reel ${this.reelId} ready to stop.`);
        let addDelayToNextReel = 0;
        this._currentReelSet = this.makeUpStopReelSet(false);
        const needToFill = this._symbolsNumInData[4] + spinStopOffset;
        let unfinishedSpinningStackedSymbolTopFillUp:string[] = this.handleUnfinishedSpinningStackedSymbols();
        let alreadyFilled = this._stopReelSetFilledBottomNum + unfinishedSpinningStackedSymbolTopFillUp.length;

        if(alreadyFilled < needToFill) {
            unfinishedSpinningStackedSymbolTopFillUp = this.makeUpStopReelSetRandomFillUp(needToFill - alreadyFilled).concat(unfinishedSpinningStackedSymbolTopFillUp);
            alreadyFilled = this._stopReelSetFilledBottomNum + unfinishedSpinningStackedSymbolTopFillUp.length;
        }
        this._currentReelSet = this._currentReelSet.concat(unfinishedSpinningStackedSymbolTopFillUp);
        spinStopOffset = alreadyFilled - this._symbolsNumInData[4];

        this._stopCounter = this._symsNumTotal + spinStopOffset;

        addDelayToNextReel += spinStopOffset * this._defaultSpinStepDuration;// TODO: change the speed to use the real speed. like near win
        // TODO: if(!this._reelPartAnimator.isQuickStop && this._reelId < (GameConfig.instance.REELS_NUM - 1)) {
        //     addDelayToNextReel += this.checkSlowMotionStopDelay();
        // }
        addDelayToNextReel += this.reelView.currentStepLeftTime;

        if(addDelayToNextReel > 0) {
            // Logger.logDev(`reel ${this.reelId} get ready to stop, add delay ${addDelayToNextReel} to next reels`);
            for(let i = this.reelId + 1; i < DeadwoodRip.reels.length; i++) {
                DeadwoodRip.reels[i]?.addStopDelayIfNotEnough(addDelayToNextReel);
            }
        }

        this.reelState = ReelState.STOPPING;
    }

    // If there are unfinished stacked symbols, fill it up.
    protected handleUnfinishedSpinningStackedSymbols():string[] {
        const lastStackedName:IParsedStackedSymbolName = this.reelView.allSymbols[0].stackedSymbolName;
        if(!lastStackedName.isStacked || lastStackedName.index === 0) {
            return [];
        }

        return ArrayHelper.initArrayWithValues(lastStackedName.index, index => `${lastStackedName.symName}*${index}`);
    }

    public isDataSymbolInValidArea(index:number):boolean {
        return index >= this._symbolsNumInData[1] && index < (this._symbolsNumInData[1] + this._symbolsNumInData[2]);
    }

    public getSymbol(index:number, isDataIndex:boolean = false, getVisibleStackedSymbol:boolean = true):DeadwoodRipSymbol {
        if(!isDataIndex) {
            index += this._symbolsNumInData[0] + this._symbolsNumInData[1];
        } else {
            index += this._symbolsNumInData[0];
        }
        const symbol = this.reelView.allSymbols[index];
        if(!symbol.stackedSymbolName.isStacked) {
            return symbol;
        }
        if(!getVisibleStackedSymbol) {
            return symbol
        }

        // return the most bottom visible symbol of a stacked symbol
        for(let i:number = symbol.stackedSymbolName!.totalNum - 1; i >= 0; i--) {
            const testSymbol = <DeadwoodRipStackedSymbol>(<DeadwoodRipStackedSymbol>symbol).stack!.symbols[i];
            if(testSymbol != null && testSymbol.parent != null) {
                return testSymbol;
            }
        }

        debugger;
        return symbol;
    }

    public replaceSymbol(newSymbol:DeadwoodRipSymbol, index:number):DeadwoodRipSymbol {
        const oldSymbol = this.getSymbol(index);
        newSymbol.position.set(oldSymbol.x, oldSymbol.y);
        this.reelView.addSymbol(newSymbol);
        this.reelView.allSymbols[this.reelView.allSymbols.indexOf(oldSymbol)] = newSymbol;
        this.reelView.removeSymbol(oldSymbol);

        return oldSymbol;
    }

    public nudge(stepsLeft:number,
                 stepDuration:number,
                 ease:string,
                 delay:number,
                 onStepStart:(stepsLeft:number, aborted:boolean) => void,
                 onStepComplete:(stepsLeft:number, aborted:boolean) => void,
                 onReelNudgeComplete:(aborted:boolean) => void,
                 abort?:boolean
    ):TimelineLite {
        if(abort != null) {
            this._abortNudge = abort;
        }
        this.reelState = ReelState.NUDGE;

        const tl = gsap.timeline();
        this._nudgeTimeline = tl;

        // reel nudge complete.
        if(stepsLeft === 0) {
            tl.add(() => {
                onReelNudgeComplete(this._abortNudge);
                this.reelState = ReelState.IDLE;
            }, delay);
            return tl;
        }

        // start nudge
        this.setNextState(ReelState.NUDGE, abort ? 0.001 : stepDuration, ease);
        tl.add(() => onStepStart(stepsLeft, this._abortNudge), delay);

        // Nudge down
        if(stepsLeft > 0) {
            tl.add(this.reelView.step(this.addNextNewSymbol(DeadwoodRipSymbolState.NORMAL), () => this.removeBottomSymbol()));
            tl.add(() => onStepComplete(stepsLeft - 1, this._abortNudge));
            tl.add(() => {
                this.nudge(stepsLeft - 1,
                    stepDuration,
                    ease,
                    0,
                    onStepStart,
                    onStepComplete,
                    onReelNudgeComplete
                );
            });
            return tl;
        }

        // Nudge up
        tl.add(this.reelView.step(this.recycleBottomSymbol(DeadwoodRipSymbolState.NORMAL), () => this.revertTopSymbol(), -this.reelView.stepDistance));
        tl.add(() => onStepComplete(stepsLeft + 1, this._abortNudge));
        tl.add(() => {
            this.nudge(stepsLeft + 1,
                stepDuration,
                ease,
                0,
                onStepStart,
                onStepComplete,
                onReelNudgeComplete
            );
        });
        return tl;
    }

    private recycleBottomSymbol(state:DeadwoodRipSymbolState):DeadwoodRipSymbol {
        const newSymbol = this._removedSymbolPool.shift()!;
        newSymbol.getStateAnimation(state, {duration : 0, alpha : 1}, {duration : 0, alpha : 0});
        this.reelView.addSymbol(newSymbol);
        StackedSymbol.updateStackedSymbols(newSymbol, ArrayHelper.fetchLastValue(this.reelView.allSymbols), 1, false);
        this.reelView.allSymbols.push(newSymbol);
        return newSymbol;
    }

    private revertTopSymbol():void {
        const removeSymbol = this.reelView.allSymbols.shift()!;
        this._currentReelSet.push(removeSymbol.symbolName);
        // Don't need to review stacked state for the top symbol.
        this.reelView.removeSymbol(removeSymbol);
        removeSymbol.onRemove();
    }

    public abortNudge():void {
        this._abortNudge = true;
        if(this._nudgeTimeline && this._nudgeTimeline.isActive()) {
            this._nudgeTimeline.progress(1);
        }
    }

    public setCurrentReelSet(reelSetName:string):void {
        const topFillUp:string[] = this.makeUpStackedSymbolTop(this.reelView.allSymbols[0].symbolName);
        this._currentFullReelSet = this.getReelSet(reelSetName);
        const randomPosition:number = this.randomizeReelSetPosition(this._currentFullReelSet);
        this._currentReelSet = this._currentFullReelSet.slice(0, randomPosition + 1).concat(topFillUp);
    }
}